import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Link } from "wouter";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  CircleDot,
  Target,
  Trophy,
  XOctagon,
  CheckCircle2,
  Calendar,
  Sparkles,
  SlidersHorizontal,
  Users,
  UserCircle,
  Package,
  MapPin,
  X,
  Landmark,
  Building2,
  UserCog,
} from "lucide-react";
import { dealTeamRoleLabel } from "@shared/crm-team";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function MetricCard({
  label,
  value,
  sub,
  icon: Icon,
  tint = "default",
  trend,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  tint?: "default" | "emerald" | "red" | "primary";
  trend?: { value: string; positive: boolean } | null;
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
            {trend && (
              <div
                className={`mt-1 text-xs flex items-center gap-1 ${
                  trend.positive ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {trend.positive ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {trend.value}
              </div>
            )}
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CrmReportsContent() {
  const { can } = usePermissions();
  const canViewAll = can("crm:deals_view_all");

  const pipelinesQuery = trpc.crm.pipelines.list.useQuery({});
  const [pipelineId, setPipelineId] = useState<number | null>(null);

  // ── Filtros ────────────────────────────────────
  type Scope = "mine" | "team";
  const [scope, setScope] = useState<Scope>(canViewAll ? "team" : "mine");
  const [ownerUserId, setOwnerUserId] = useState<string>("all");
  const [productId, setProductId] = useState<string>("all");
  const [contactType, setContactType] = useState<string>("all");
  const [state, setState] = useState<string>("all");
  const [city, setCity] = useState<string>("all");
  const now = new Date();
  const [month, setMonth] = useState<string>("all");
  const [year, setYear] = useState<string>(String(now.getFullYear()));

  useEffect(() => {
    if (!pipelineId && (pipelinesQuery.data?.length ?? 0) > 0) {
      const def = pipelinesQuery.data!.find((p: any) => p.isDefault) ?? pipelinesQuery.data![0];
      setPipelineId(def.id);
    }
  }, [pipelinesQuery.data, pipelineId]);

  // Sources para os selects de filtro
  const sellersQuery = trpc.crm.sellers.list.useQuery({}, { enabled: canViewAll });
  const productsQuery = trpc.crm.products.list.useQuery({ limit: 500 });
  const organizationsQuery = trpc.crm.organizations.list.useQuery({});

  // Quando scope=mine, força ownerUserId="all" (backend força para o usuário)
  // Quando scope=team com view_all, permite escolher um vendedor específico ou "all"
  const effectiveOwner =
    !canViewAll || scope === "mine"
      ? undefined
      : ownerUserId !== "all"
      ? Number(ownerUserId)
      : undefined;

  const filterBase = {
    pipelineId: pipelineId ?? 0,
    ownerUserId: effectiveOwner,
    productId: productId !== "all" ? Number(productId) : undefined,
    contactType: contactType !== "all" ? (contactType as any) : undefined,
    state: state !== "all" ? state : undefined,
    city: city !== "all" ? city : undefined,
    referenceMonth: month !== "all" ? Number(month) : undefined,
    referenceYear: Number(year),
  };

  /**
   * Janela do relatório por papel, derivada do mesmo filtro de mês/ano do resto
   * da página. "Todos os meses" vira o ano inteiro — o endpoint recorta por
   * `deals.createdAt`, ou seja, conta a negociação no período em que ela
   * nasceu, que é quando o SDR fez o trabalho dele.
   */
  const janelaPapeis = (() => {
    const ano = Number(year);
    if (month === "all") {
      return { from: new Date(ano, 0, 1), to: new Date(ano, 11, 31, 23, 59, 59, 999) };
    }
    const m = Number(month) - 1;
    return { from: new Date(ano, m, 1), to: new Date(ano, m + 1, 0, 23, 59, 59, 999) };
  })();

  const teamRolesQuery = trpc.crm.reports.teamRolePerformance.useQuery(
    {
      pipelineId: pipelineId ?? undefined,
      from: janelaPapeis.from,
      to: janelaPapeis.to,
    },
    { enabled: canViewAll && !!pipelineId },
  );

  const summaryQuery = trpc.crm.reports.pipelineSummary.useQuery(
    pipelineId ? filterBase : (undefined as any),
    { enabled: !!pipelineId },
  );

  const kanbanQuery = trpc.crm.deals.kanban.useQuery(
    pipelineId
      ? {
          pipelineId,
          ownerUserId: effectiveOwner,
          productId:
            filterBase.productId !== undefined ? filterBase.productId : undefined,
          contactType: filterBase.contactType as any,
          // kanban não tem state/city/period hoje — filtros parciais ok
        }
      : (undefined as any),
    { enabled: !!pipelineId },
  );

  const wonDeals = trpc.crm.deals.list.useQuery(
    pipelineId
      ? {
          pipelineId,
          status: "won",
          ownerUserId: effectiveOwner,
          productId:
            filterBase.productId !== undefined ? filterBase.productId : undefined,
          contactType: filterBase.contactType as any,
          limit: 5,
        }
      : (undefined as any),
    { enabled: !!pipelineId },
  );

  // Top produtos vendidos (respeita todos os filtros — só considera deals ganhos)
  const topProductsQuery = trpc.crm.reports.topProducts.useQuery(
    pipelineId
      ? {
          pipelineId,
          status: "won",
          ownerUserId: effectiveOwner,
          contactType: filterBase.contactType as any,
          state: filterBase.state,
          city: filterBase.city,
          referenceMonth: filterBase.referenceMonth,
          referenceYear: filterBase.referenceYear,
          limit: 10,
        }
      : (undefined as any),
    { enabled: !!pipelineId },
  );

  const activeFilterCount =
    (ownerUserId !== "all" && canViewAll && scope === "team" ? 1 : 0) +
    (productId !== "all" ? 1 : 0) +
    (contactType !== "all" ? 1 : 0) +
    (state !== "all" ? 1 : 0) +
    (city !== "all" ? 1 : 0) +
    (month !== "all" ? 1 : 0);

  function clearFilters() {
    setOwnerUserId("all");
    setProductId("all");
    setContactType("all");
    setState("all");
    setCity("all");
    setMonth("all");
    setYear(String(now.getFullYear()));
  }

  const stages = kanbanQuery.data?.stages ?? [];
  const dealsByStage = (kanbanQuery.data?.dealsByStage ?? {}) as Record<number, any[]>;

  const pipelineStats = useMemo(() => {
    const stats = stages.map((s: any) => {
      const list = dealsByStage[s.id] ?? [];
      const sum = list.reduce((acc: number, d: any) => acc + Number(d.deal.value ?? 0), 0);
      return { stage: s, count: list.length, sum };
    });
    const maxSum = Math.max(1, ...stats.map((s) => s.sum));
    return { stats, maxSum };
  }, [stages, dealsByStage]);

  const wonCount = summaryQuery.data?.wonCount ?? 0;
  const lostCount = summaryQuery.data?.lostCount ?? 0;
  const openCount = summaryQuery.data?.openCount ?? 0;
  const totalOpen = summaryQuery.data?.totalValue ?? 0;
  const conv = wonCount + lostCount > 0 ? (wonCount / (wonCount + lostCount)) * 100 : null;
  const avgTicket = openCount > 0 ? Number(totalOpen) / openCount : 0;

  const isLoading = summaryQuery.isLoading || kanbanQuery.isLoading;

  return (
    <div className="space-y-5 max-w-6xl">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <BarChart2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate flex items-center gap-2">
              Relatórios
              <Badge
                variant="secondary"
                className="text-[10px] h-5 font-medium uppercase tracking-wide"
              >
                {scope === "mine" ? (
                  <>
                    <UserCircle className="h-3 w-3 mr-1" />
                    Minhas
                  </>
                ) : (
                  <>
                    <Users className="h-3 w-3 mr-1" />
                    Equipe
                  </>
                )}
              </Badge>
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {scope === "mine"
                ? "Apenas suas oportunidades"
                : canViewAll
                ? "Todas as oportunidades da equipe"
                : "Apenas suas oportunidades (sem permissão para ver todas)"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {canViewAll && (
            <div className="inline-flex items-center gap-1 p-0.5 bg-muted/60 rounded-md">
              <button
                onClick={() => setScope("mine")}
                className={`px-2.5 py-1 text-xs font-medium rounded transition ${
                  scope === "mine"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Minhas
              </button>
              <button
                onClick={() => setScope("team")}
                className={`px-2.5 py-1 text-xs font-medium rounded transition ${
                  scope === "team"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Equipe
              </button>
            </div>
          )}

          <Select
            value={pipelineId ? String(pipelineId) : ""}
            onValueChange={(v) => setPipelineId(Number(v))}
          >
            <SelectTrigger className="h-9 w-auto min-w-[160px]">
              <SelectValue placeholder="Funil" />
            </SelectTrigger>
            <SelectContent>
              {(pipelinesQuery.data ?? []).map((p: any) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name}
                  {p.isDefault && (
                    <Sparkles className="h-3 w-3 inline ml-1 opacity-60" />
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ReportsFiltersPopover
            canViewAll={canViewAll && scope === "team"}
            ownerUserId={ownerUserId}
            productId={productId}
            contactType={contactType}
            state={state}
            city={city}
            month={month}
            year={year}
            activeCount={activeFilterCount}
            sellers={sellersQuery.data ?? []}
            products={(productsQuery.data?.rows ?? []) as any[]}
            states={organizationsQuery.data?.states ?? []}
            cities={organizationsQuery.data?.cities ?? []}
            onOwnerUserId={setOwnerUserId}
            onProductId={setProductId}
            onContactType={setContactType}
            onState={(v) => {
              setState(v);
              setCity("all");
            }}
            onCity={setCity}
            onMonth={setMonth}
            onYear={setYear}
            onClear={clearFilters}
          />
        </div>
      </div>

      {/* Chips de filtros ativos */}
      {activeFilterCount > 0 && (
        <ReportsActiveChips
          canViewAll={canViewAll && scope === "team"}
          ownerUserId={ownerUserId}
          productId={productId}
          contactType={contactType}
          state={state}
          city={city}
          month={month}
          year={year}
          sellers={sellersQuery.data ?? []}
          products={(productsQuery.data?.rows ?? []) as any[]}
          onOwnerUserId={setOwnerUserId}
          onProductId={setProductId}
          onContactType={setContactType}
          onState={setState}
          onCity={setCity}
          onMonth={setMonth}
        />
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : (
        <>
          {/* ── KPIs ────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard
              label="Pipeline aberto"
              value={formatCompact(totalOpen)}
              sub={`${openCount} oportunidade${openCount === 1 ? "" : "s"}`}
              icon={Target}
              tint="primary"
            />
            <MetricCard
              label="Ticket médio"
              value={formatCompact(avgTicket)}
              sub={openCount > 0 ? "por oportunidade" : "—"}
              icon={CircleDot}
            />
            <MetricCard
              label="Fechados"
              value={String(wonCount)}
              sub={lostCount > 0 ? `${lostCount} perdidos` : "—"}
              icon={Trophy}
              tint="emerald"
            />
            <MetricCard
              label="Conversão"
              value={conv === null ? "—" : `${conv.toFixed(0)}%`}
              sub="ganho / (ganho + perdido)"
              icon={CheckCircle2}
              tint={conv !== null && conv >= 50 ? "emerald" : "red"}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* ── Funnel bars (2 cols) ──────────── */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart2 className="h-4 w-4" />
                  Distribuição por estágio
                  <Badge variant="secondary" className="ml-auto">
                    {openCount} abertas
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pipelineStats.stats.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    Sem dados.
                  </div>
                ) : (
                  pipelineStats.stats.map(({ stage, count, sum }) => {
                    const pct = (sum / pipelineStats.maxSum) * 100;
                    const isTerminal = stage.isWon || stage.isLost;
                    return (
                      <div key={stage.id} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: stage.color }}
                            />
                            <span className="font-medium truncate">{stage.name}</span>
                            {stage.isWon && (
                              <Trophy className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                            )}
                            {stage.isLost && (
                              <XOctagon className="h-3.5 w-3.5 text-red-600 shrink-0" />
                            )}
                            <Badge variant="secondary" className="text-[10px] h-5">
                              {count}
                            </Badge>
                          </div>
                          <span className="font-mono tabular-nums text-xs">
                            {formatBRL(sum)}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: stage.color,
                              minWidth: count > 0 ? "6px" : "0",
                              opacity: isTerminal ? 0.5 : 1,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* ── Últimos fechamentos ───────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-emerald-600" />
                  Últimos fechamentos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {wonDeals.isLoading ? (
                  <>
                    <Skeleton className="h-14 w-full" />
                    <Skeleton className="h-14 w-full" />
                  </>
                ) : (wonDeals.data?.rows ?? []).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    Nenhum deal fechado ainda. Vamos lá!
                  </div>
                ) : (
                  (wonDeals.data?.rows ?? []).map((row: any) => {
                    const contactFull = [row.contact?.name, row.contact?.lastName]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <Link
                        key={row.deal.id}
                        href={`/crm/deals/${row.deal.id}`}
                        className="block rounded-md border p-2.5 hover:bg-muted/40 transition"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium truncate">
                            {row.deal.title}
                          </div>
                          <Badge className="bg-emerald-600 shrink-0 text-[10px]">
                            <Trophy className="h-2.5 w-2.5 mr-1" />
                            Fechada
                          </Badge>
                        </div>
                        <div className="text-base font-bold tabular-nums mt-1">
                          {formatBRL(row.deal.value)}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          {row.owner?.name && (
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                                {initials(row.owner.name)}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <span className="truncate">
                            {contactFull || row.contact?.companyName || "—"}
                          </span>
                          {row.deal.closedAt && (
                            <span className="ml-auto flex items-center gap-1 shrink-0">
                              <Calendar className="h-3 w-3" />
                              {new Date(row.deal.closedAt).toLocaleDateString("pt-BR", {
                                day: "2-digit",
                                month: "short",
                              })}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Top produtos vendidos ───────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Top produtos vendidos
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  deals ganhos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {topProductsQuery.isLoading ? (
                <div className="p-4 space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (topProductsQuery.data ?? []).length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  Nenhum produto vendido ainda no período filtrado.
                </div>
              ) : (
                <div className="divide-y">
                  {/* Header */}
                  <div className="grid grid-cols-[40px_1fr_auto_auto_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium bg-muted/40">
                    <div>#</div>
                    <div>Produto</div>
                    <div className="text-right">Qtd</div>
                    <div className="text-right">Receita</div>
                    <div className="text-right">Ticket médio</div>
                  </div>
                  {/* Rows */}
                  {(topProductsQuery.data ?? []).map((row: any, idx: number) => {
                    const topRevenue = Number(
                      (topProductsQuery.data?.[0] as any)?.revenue ?? 0,
                    );
                    const pct =
                      topRevenue > 0
                        ? (Number(row.revenue) / topRevenue) * 100
                        : 0;
                    return (
                      <div
                        key={row.productId}
                        className="relative grid grid-cols-[40px_1fr_auto_auto_auto] gap-3 px-4 py-3 items-center hover:bg-muted/30 transition"
                      >
                        {/* Background bar */}
                        <div
                          className="absolute inset-y-0 left-0 bg-primary/5 pointer-events-none"
                          style={{ width: `${pct}%` }}
                        />
                        <div className="relative text-sm font-bold text-muted-foreground tabular-nums">
                          {idx + 1}
                        </div>
                        <div className="relative flex items-center gap-3 min-w-0">
                          {row.imageUrl ? (
                            <img
                              src={row.imageUrl}
                              alt=""
                              className="h-9 w-9 rounded-md object-cover border shrink-0"
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0 border">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {row.name}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                              {row.sku && (
                                <>
                                  <span className="font-mono">{row.sku}</span>
                                  <span>·</span>
                                </>
                              )}
                              <span>
                                {row.dealCount} deal
                                {Number(row.dealCount) === 1 ? "" : "s"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="relative text-sm font-semibold tabular-nums text-right">
                          {Number(row.quantity).toLocaleString("pt-BR", {
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div className="relative text-sm font-bold tabular-nums text-right text-emerald-700 dark:text-emerald-400">
                          {formatBRL(row.revenue)}
                        </div>
                        <div className="relative text-xs tabular-nums text-right text-muted-foreground">
                          {formatBRL(row.avgTicket)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Desempenho por papel do time ────────────────────────── */}
          {canViewAll && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  Desempenho por papel do time
                  <Badge variant="secondary" className="ml-auto text-[10px]">
                    por criação da oportunidade
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {teamRolesQuery.isLoading ? (
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : (teamRolesQuery.data ?? []).length === 0 ? (
                  <div className="py-10 px-4 text-center text-sm text-muted-foreground">
                    Ninguém marcado como SDR, Closer ou Hunter nas oportunidades
                    do período. Registre o time na aba <strong>Time</strong> de
                    cada negociação para acompanhar quem originou o quê.
                  </div>
                ) : (
                  <div className="divide-y">
                    <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium bg-muted/40">
                      <div>Pessoa / papel</div>
                      <div className="text-right">Oportunidades</div>
                      <div className="text-right">Ganhas</div>
                      <div className="text-right">Conversão</div>
                      <div className="text-right">Receita</div>
                    </div>
                    {(teamRolesQuery.data ?? []).map((row: any) => {
                      // Denominador é o que já FECHOU: contar as abertas como
                      // derrota puniria quem acabou de originar a oportunidade.
                      const fechadas = row.won + row.lost;
                      const conv = fechadas > 0 ? Math.round((row.won / fechadas) * 100) : null;
                      return (
                        <div
                          key={`${row.userId}-${row.role}`}
                          className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-4 py-3 items-center text-sm"
                        >
                          <div className="min-w-0">
                            <p className="font-medium truncate">{row.userName ?? "—"}</p>
                            <p className="text-xs text-muted-foreground">
                              {dealTeamRoleLabel(row.role)}
                            </p>
                          </div>
                          <div className="text-right tabular-nums">{row.deals}</div>
                          <div className="text-right tabular-nums font-medium">{row.won}</div>
                          <div className="text-right tabular-nums text-muted-foreground">
                            {conv === null ? "—" : `${conv}%`}
                          </div>
                          <div className="text-right tabular-nums">{formatBRL(row.wonValue)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Popover de filtros ──────────────────────────────────────────────────────
const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function ReportsFiltersPopover({
  canViewAll,
  ownerUserId,
  productId,
  contactType,
  state,
  city,
  month,
  year,
  activeCount,
  sellers,
  products,
  states,
  cities,
  onOwnerUserId,
  onProductId,
  onContactType,
  onState,
  onCity,
  onMonth,
  onYear,
  onClear,
}: {
  canViewAll: boolean;
  ownerUserId: string;
  productId: string;
  contactType: string;
  state: string;
  city: string;
  month: string;
  year: string;
  activeCount: number;
  sellers: any[];
  products: any[];
  states: string[];
  cities: string[];
  onOwnerUserId: (v: string) => void;
  onProductId: (v: string) => void;
  onContactType: (v: string) => void;
  onState: (v: string) => void;
  onCity: (v: string) => void;
  onMonth: (v: string) => void;
  onYear: (v: string) => void;
  onClear: () => void;
}) {
  const now = new Date();
  const yearOptions = Array.from({ length: 5 }).map((_, i) =>
    String(now.getFullYear() - i),
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={activeCount > 0 ? "default" : "outline"}
          className="h-9"
        >
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
          Filtros
          {activeCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-1.5 h-4 px-1.5 text-[10px] bg-background/20 text-background"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="text-sm font-semibold">Filtros</h4>
          {activeCount > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClear}>
              Limpar
            </Button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4 space-y-4">
          {/* Período */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Período
            </Label>
            <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2">
              <Select value={month} onValueChange={onMonth}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Ano todo</SelectItem>
                  {MONTHS_PT.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={year} onValueChange={onYear}>
                <SelectTrigger className="h-9 w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={y}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Vendedor */}
          {canViewAll && (
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Trophy className="h-3 w-3" />
                Vendedor
              </Label>
              <Select value={ownerUserId} onValueChange={onOwnerUserId}>
                <SelectTrigger className="h-9 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {sellers.map((s: any) => (
                    <SelectItem key={s.profile.userId} value={String(s.profile.userId)}>
                      {s.user.name ?? s.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Produto */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" />
              Produto
            </Label>
            <Select value={productId} onValueChange={onProductId}>
              <SelectTrigger className="h-9 mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {products.map((row: any) => (
                  <SelectItem key={row.product.id} value={String(row.product.id)}>
                    {row.product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tipo */}
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Tipo de contato
            </Label>
            <Select value={contactType} onValueChange={onContactType}>
              <SelectTrigger className="h-9 mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="public_admin">
                  <span className="flex items-center gap-2">
                    <Landmark className="h-3.5 w-3.5 text-amber-600" />
                    B2G · Órgão público
                  </span>
                </SelectItem>
                <SelectItem value="empresa">
                  <span className="flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-sky-600" />
                    B2B · Empresa
                  </span>
                </SelectItem>
                <SelectItem value="cliente_final">
                  <span className="flex items-center gap-2">
                    <UserCircle className="h-3.5 w-3.5 text-emerald-600" />
                    B2C · Pessoa
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Estado e Cidade */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Estado
              </Label>
              <Select value={state} onValueChange={onState}>
                <SelectTrigger className="h-9 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {states.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Cidade
              </Label>
              <Select
                value={city}
                onValueChange={onCity}
                disabled={cities.length === 0}
              >
                <SelectTrigger className="h-9 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ReportsActiveChips({
  canViewAll,
  ownerUserId,
  productId,
  contactType,
  state,
  city,
  month,
  year,
  sellers,
  products,
  onOwnerUserId,
  onProductId,
  onContactType,
  onState,
  onCity,
  onMonth,
}: {
  canViewAll: boolean;
  ownerUserId: string;
  productId: string;
  contactType: string;
  state: string;
  city: string;
  month: string;
  year: string;
  sellers: any[];
  products: any[];
  onOwnerUserId: (v: string) => void;
  onProductId: (v: string) => void;
  onContactType: (v: string) => void;
  onState: (v: string) => void;
  onCity: (v: string) => void;
  onMonth: (v: string) => void;
}) {
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (month !== "all") {
    const m = MONTHS_PT[Number(month) - 1];
    chips.push({
      key: "month",
      label: `${m} ${year}`,
      onRemove: () => onMonth("all"),
    });
  }
  if (canViewAll && ownerUserId !== "all") {
    const s = sellers.find((x: any) => String(x.profile.userId) === ownerUserId);
    chips.push({
      key: "owner",
      label: s?.user?.name ?? "Vendedor",
      onRemove: () => onOwnerUserId("all"),
    });
  }
  if (productId !== "all") {
    const p = products.find((x: any) => String(x.product.id) === productId);
    chips.push({
      key: "product",
      label: p?.product?.name ?? "Produto",
      onRemove: () => onProductId("all"),
    });
  }
  if (contactType !== "all") {
    const map: Record<string, string> = {
      public_admin: "B2G · Órgão",
      empresa: "B2B · Empresa",
      cliente_final: "B2C · Pessoa",
    };
    chips.push({
      key: "type",
      label: map[contactType] ?? contactType,
      onRemove: () => onContactType("all"),
    });
  }
  if (state !== "all") {
    chips.push({
      key: "state",
      label: `UF: ${state}`,
      onRemove: () => onState("all"),
    });
  }
  if (city !== "all") {
    chips.push({
      key: "city",
      label: city,
      onRemove: () => onCity("all"),
    });
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/30"
        >
          {c.label}
          <button
            onClick={c.onRemove}
            className="h-4 w-4 rounded-full hover:bg-primary/20 flex items-center justify-center"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function CrmReports() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:reports_view">
        <CrmReportsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
